"use client";

import { useIsEmbed } from "@calcom/embed-core/embed-iframe";
import { getNativeLanguageName, withBookingLanguage } from "@calcom/lib/bookingLanguages";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import classNames from "@calcom/ui/classNames";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { MouseEvent } from "react";
import { rememberBookingLanguage, useBookingLanguage } from "./BookingLanguageProvider";
import { LanguageFlag } from "./LanguageFlag";

export function BookingLanguageSwitcher({ className }: { className?: string }) {
  const bookingLanguage = useBookingLanguage();
  const isEmbed = useIsEmbed();

  // An embedding site has its own language selector and passes the language in the embed URL
  if (!bookingLanguage || bookingLanguage.languages.length < 2 || isEmbed) return null;

  // The platform Booker renders this component too, without the app router the links below need
  return (
    <LanguageLinks
      languages={bookingLanguage.languages}
      locale={bookingLanguage.locale}
      className={className}
    />
  );
}

function LanguageLinks({
  languages,
  locale,
  className,
}: {
  languages: string[];
  locale: string;
  className?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useLocale();

  const query = searchParams?.toString();
  const currentHref = `${pathname}${query ? `?${query}` : ""}`;

  const switchTo = (event: MouseEvent<HTMLAnchorElement>, language: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    rememberBookingLanguage(language);
    if (language === locale) return;
    const { pathname: path, search, hash } = window.location;
    // A soft navigation keeps the selected date, time and typed answers, which live in client state
    router.replace(withBookingLanguage(`${path}${search}${hash}`, language), { scroll: false });
  };

  return (
    <nav
      aria-label={t("language")}
      data-testid="booking-language-switcher"
      className={classNames("flex items-center gap-3 text-xs font-medium uppercase", className)}>
      {languages.map((language) => {
        const isActive = language === locale;
        const name = getNativeLanguageName(language);
        return (
          <a
            key={language}
            href={withBookingLanguage(currentHref, language)}
            hrefLang={language}
            lang={language}
            title={name}
            aria-label={name}
            aria-current={isActive ? "true" : undefined}
            onClick={(event) => switchTo(event, language)}
            // The Booker writes the date and time into the URL without Next.js seeing it, so a new-tab or copied link reads it here
            onPointerDown={(event) => {
              const { pathname: path, search, hash } = window.location;
              event.currentTarget.href = withBookingLanguage(`${path}${search}${hash}`, language);
            }}
            className={classNames(
              "inline-flex items-center gap-1.5 py-1 decoration-2 underline-offset-[6px] transition-colors",
              isActive ? "text-emphasis underline" : "text-subtle hover:text-emphasis"
            )}>
            <LanguageFlag locale={language} />
            {language}
          </a>
        );
      })}
    </nav>
  );
}
